import { cn } from '@pesto/utils';
import { forwardRef } from 'react';

interface ToggleGroupOption {
  label: string;
  value: string;
  selectedClassName?: string;
}

export interface ToggleGroupProps {
  options: ToggleGroupOption[];
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  name?: string;
}

export const ToggleGroup = forwardRef<HTMLDivElement, ToggleGroupProps>(
  ({ options, value, onChange, disabled, className, name, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        role="radiogroup"
        aria-label={name}
        className={cn(
          'bg-muted text-muted-foreground inline-flex h-10 items-center justify-center rounded-md p-1',
          className,
        )}
        {...rest}
      >
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
                'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-50',
                isSelected && cn('bg-background text-foreground shadow-sm', option.selectedClassName),
              )}
              onClick={() => onChange?.(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  },
);

ToggleGroup.displayName = 'ToggleGroup';
